import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import { InvoiceInbox } from "@/components/business/InvoiceInbox";
import { MailboxImport } from "@/components/business/MailboxImport";
import { SupplierDetail } from "@/components/business/SupplierDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const NewSupplier = ({ cid, onDone }) => {
  const [v, setV] = useState({ name: "", email: "", alias: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/business/companies/${cid}/suppliers`, { name: v.name, email: v.email || null, aliases: v.alias ? [v.alias] : [] }); toast.success("Fournisseur créé"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="card-surface grid gap-2 p-4 sm:grid-cols-4" data-testid="supplier-form">
      <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Nom du fournisseur" required minLength={2} data-testid="supplier-name" />
      <Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} placeholder="E-mail (facultatif)" data-testid="supplier-email" />
      <Input value={v.alias} onChange={(e) => setV({ ...v, alias: e.target.value })} placeholder="Libellé bancaire (ex. PRLV EDF)" data-testid="supplier-alias" />
      <Button type="submit" className="rounded-full" data-testid="supplier-submit">Créer</Button>
    </form>
  );
};

const Suppliers = ({ company }) => {
  const qc = useQueryClient();
  const { money, date } = useFormat();
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  const { data } = useQuery({ queryKey: ["biz-suppliers", company.id], queryFn: () => api.get(`/business/companies/${company.id}/suppliers`).then((r) => r.data) });
  if (!data) return <Skeleton className="h-60 rounded-2xl" />;
  const canEdit = company.my_role !== "viewer";
  return (
    <div className="space-y-4" data-testid="supplier-list">
      {canEdit && <Button variant="outline" className="rounded-full" onClick={() => setAdding(!adding)} data-testid="supplier-add"><Plus className="mr-1 h-4 w-4" />Ajouter un fournisseur</Button>}
      {adding && <NewSupplier cid={company.id} onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["biz-suppliers"] }); }} />}
      {data.length ? <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{data.map((s) => (
        <li key={s.id}><button type="button" onClick={() => setOpen(s.id)} className="card-surface lift block w-full space-y-2 p-4 text-left" data-testid={`supplier-${s.id}`}>
          <div className="flex items-center gap-2"><span className="tone-chip tone-lilac flex h-9 w-9 items-center justify-center rounded-xl"><Building2 className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1"><div className="truncate font-bold">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.stats.since ? `Fournisseur depuis ${new Date(`${s.stats.since}T12:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "numeric" })}` : "Aucun paiement"}{s.info?.siren ? ` · SIREN ${s.info.siren}` : ""}</div></div></div>
          <div className="flex items-end justify-between text-xs"><div><div className="text-muted-foreground">Total payé</div><div className="num text-base font-extrabold">{money(s.stats.total, { maximumFractionDigits: 0 })}</div></div>
            <div className="text-right"><div className="text-muted-foreground">Cette année</div><div className="num font-bold">{money(s.stats.this_year, { maximumFractionDigits: 0 })}</div></div></div>
          {s.stats.missing > 0 && <div className="text-[11px] font-bold text-destructive" data-testid={`supplier-missing-${s.id}`}>{s.stats.missing} facture(s) manquante(s)</div>}
        </button></li>))}</ul>
        : <p className="card-surface p-6 text-sm text-muted-foreground" data-testid="supplier-empty">Déposez des factures : chaque nouveau fournisseur aura sa fiche automatiquement.</p>}
      <SupplierDetail cid={company.id} sid={open} canEdit={canEdit} onClose={() => setOpen(null)} />
    </div>
  );
};

export const BizSuppliers = ({ company }) => {
  const [tab, setTab] = useState("invoices");
  return (
    <div className="space-y-5" data-testid="biz-suppliers">
      <MailboxImport company={company} />
      <div className="flex gap-2">{[["invoices", "Factures déposées"], ["suppliers", "Fiches fournisseurs"]].map(([k, l]) => (
        <Button key={k} variant={k === tab ? "default" : "outline"} className="rounded-full" onClick={() => setTab(k)} data-testid={`suppliers-tab-${k}`}>{l}</Button>))}</div>
      {tab === "invoices" ? <InvoiceInbox company={company} /> : <Suppliers company={company} />}
    </div>
  );
};
